import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@/lib/zod-resolver";
import { z } from "zod";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Mail, ArrowRight, KeyRound, CheckCircle2, UserPlus } from "lucide-react";
import { SpnLogo } from "@/components/brand/spn-logo";
import {
  useGetMe,
  useRequestMagicLink,
  useVerifyMagicLink,
  useCreateAccessRequest,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Spinner } from "@/components/ui/spinner";
import { getFunnelContext, trackFunnelEvent } from "@/lib/funnel-analytics";

const signInSchema = z.object({ email: z.string().email("Enter a valid email") });
type SignInValues = z.infer<typeof signInSchema>;

const requestSchema = z.object({
  email: z.string().email("Enter a valid email"),
  name: z.string().trim().max(200).optional().or(z.literal("")),
  company: z.string().trim().max(200).optional().or(z.literal("")),
  ueiCode: z.string().trim().max(32).optional().or(z.literal("")),
  cageCode: z.string().trim().max(32).optional().or(z.literal("")),
  note: z.string().trim().max(2000).optional().or(z.literal("")),
  website: z.string().max(200).optional().or(z.literal("")),
});
type RequestValues = z.infer<typeof requestSchema>;

export default function LoginPage() {
  const [, navigate] = useLocation();
  const qc = useQueryClient();
  const { data: me, isLoading } = useGetMe();
  const funnelContext = getFunnelContext();
  const [devToken, setDevToken] = useState<string | null>(null);
  const [sentEmail, setSentEmail] = useState<string | null>(null);
  const [accessRequiredEmail, setAccessRequiredEmail] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"signin" | "request">(() =>
    new URLSearchParams(window.location.search).get("tab") === "request"
      ? "request"
      : "signin",
  );
  const [requestState, setRequestState] = useState<{
    status: "pending" | "approved" | "rejected";
    alreadyExisted: boolean;
    email: string;
  } | null>(null);

  const signInForm = useForm<SignInValues>({
    resolver: zodResolver(signInSchema),
    defaultValues: { email: "" },
  });

  const requestForm = useForm<RequestValues>({
    resolver: zodResolver(requestSchema),
    defaultValues: {
      email: "",
      name: "",
      company: "",
      ueiCode: "",
      cageCode: "",
      note: "",
      website: "",
    },
  });

  const requestMut = useRequestMagicLink({
    mutation: {
      onSuccess: (res, vars) => {
        if (res.accessRequired) {
          setSentEmail(null);
          setDevToken(null);
          setAccessRequiredEmail(vars.data.email);
          requestForm.setValue("email", vars.data.email);
          toast.info("This email isn't approved yet — request access on the other tab.");
          return;
        }
        setAccessRequiredEmail(null);
        setSentEmail(vars.data.email);
        if (res.emailed) {
          setDevToken(null);
          toast.success("Magic link sent to your inbox");
        } else if (res.devToken) {
          setDevToken(res.devToken);
          toast.success("Magic link issued (dev token ready — email not configured)");
        } else {
          toast.success("Magic link issued");
        }
      },
      onError: (e) => toast.error(e?.message ?? "Could not send link"),
    },
  });

  const getNextParam = (): string | null => {
    const n = new URLSearchParams(window.location.search).get("next");
    return n && n.startsWith("/") ? n : null;
  };

  const verifyMut = useVerifyMagicLink({
    mutation: {
      onSuccess: () => {
        trackFunnelEvent("first_successful_sign_in", "verify");
        qc.invalidateQueries();
        toast.success("Signed in");
        const next = getNextParam();
        navigate(next ?? "/dashboard");
      },
      onError: (e) => toast.error(e?.message ?? "Could not verify token"),
    },
  });

  const createAccessMut = useCreateAccessRequest({
    mutation: {
      onSuccess: (res, vars) => {
        setRequestState({
          status: res.status,
          alreadyExisted: !!res.alreadyExisted,
          email: vars.data.email,
        });
        if (res.status === "approved") {
          toast.success("You're already approved — request a sign-in link.");
        } else if (res.alreadyExisted) {
          toast.message("We've already got your request — sit tight.");
        } else {
          toast.success("Thanks — we'll email you when you're approved.");
        }
      },
      onError: (e) => toast.error(e?.message ?? "Could not submit request"),
    },
  });

  useEffect(() => {
    trackFunnelEvent("login_view", "login");
  }, []);

  useEffect(() => {
    if (activeTab === "request") trackFunnelEvent("request_form_start", "login");
  }, [activeTab]);

  useEffect(() => {
    if (me?.user) {
      const next = getNextParam();
      navigate(next ?? "/dashboard");
    }
  }, [me, navigate]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner className="h-6 w-6 text-primary" />
      </div>
    );
  }

  const onSignIn = (data: SignInValues) => {
    setDevToken(null);
    setAccessRequiredEmail(null);
    requestMut.mutate({ data: { email: data.email, next: getNextParam() ?? undefined } });
  };

  const onRequestAccess = (data: RequestValues) => {
    createAccessMut.mutate({
      data: {
        email: data.email,
        name: data.name || null,
        company: data.company || null,
        ueiCode: data.ueiCode || null,
        cageCode: data.cageCode || null,
        note: data.note || null,
        website: data.website || null,
        analyticsId: funnelContext.anonymousId,
        source: funnelContext.source,
        campaign: funnelContext.campaign,
        variant: funnelContext.variant,
      },
    });
  };

  const onRequestAccessInvalid = () => trackFunnelEvent("validation_error", "login");

  const switchToRequestTab = () => {
    setActiveTab("request");
    if (accessRequiredEmail) requestForm.setValue("email", accessRequiredEmail);
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-100 via-white to-blue-50">
      <div className="pointer-events-none absolute inset-0 [background:radial-gradient(900px_circle_at_85%_-10%,hsl(217_91%_60%/0.18),transparent_60%),radial-gradient(700px_circle_at_-10%_110%,hsl(199_89%_48%/0.18),transparent_60%)]" />
      <div className="relative grid min-h-screen lg:grid-cols-2">
        <div className="hidden flex-col justify-between p-12 lg:flex">
          <a href={import.meta.env.BASE_URL} className="flex items-center gap-3">
            <SpnLogo className="h-10 w-10" />
            <div className="leading-tight">
              <div className="text-base font-semibold">Synergistic Partner Network</div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">
                GovCon Teaming Platform
              </div>
            </div>
          </a>

          <div className="space-y-6 max-w-lg">
            <h1 className="text-4xl font-semibold tracking-tight text-slate-900">
              Find the right teammates for your next federal pursuit.
            </h1>
            <p className="text-lg text-slate-600">
              SPN gives GovCon vendors a structured place to publish capabilities, prove agency
              experience, and form winning teams — without the capability statement guessing game.
            </p>
            <ul className="space-y-3 text-sm text-slate-700">
              <li className="flex items-start gap-2">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary" />
                Verified agency signals with tiered visibility controls.
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary" />
                AI-assisted extraction from your capability statements.
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary" />
                Direct teaming requests &amp; private conversations.
              </li>
            </ul>
          </div>

          <div className="text-xs text-slate-500">
            © {new Date().getFullYear()} Synergistic Partner Network
          </div>
        </div>

        <div className="flex items-center justify-center p-6 lg:p-12">
          <Card className="w-full max-w-md shadow-xl">
            <CardHeader>
              <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Mail className="h-5 w-5" />
              </div>
              <CardTitle className="text-2xl">Welcome to SPN</CardTitle>
              <CardDescription>
                Sign in if you're already approved, or request free access to get started.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs
                value={activeTab}
                onValueChange={(v) => setActiveTab(v as "signin" | "request")}
              >
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="signin" data-testid="tab-signin">
                    Sign in
                  </TabsTrigger>
                  <TabsTrigger value="request" data-testid="tab-request">
                    Request access
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="signin" className="space-y-4 pt-4">
                  <form onSubmit={signInForm.handleSubmit(onSignIn)} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="signin-email">Email</Label>
                      <Input
                        id="signin-email"
                        type="email"
                        placeholder="you@company.com"
                        autoComplete="email"
                        data-testid="input-signin-email"
                        {...signInForm.register("email")}
                      />
                      {signInForm.formState.errors.email && (
                        <p className="text-sm text-destructive">
                          {signInForm.formState.errors.email.message}
                        </p>
                      )}
                    </div>

                    {accessRequiredEmail && (
                      <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                        <div className="font-medium">This email isn't approved yet</div>
                        <p className="text-amber-800">
                          Ask for access on the other tab — we'll email you a sign-in link once an
                          admin approves it.
                        </p>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="gap-2 border-amber-300 bg-white"
                          onClick={switchToRequestTab}
                          data-testid="button-switch-to-request"
                        >
                          <UserPlus className="h-3.5 w-3.5" />
                          Request access
                        </Button>
                      </div>
                    )}

                    {sentEmail && !devToken && !accessRequiredEmail && (
                      <div className="rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
                        A sign-in link has been sent to{" "}
                        <span className="font-medium text-foreground">{sentEmail}</span>.
                      </div>
                    )}

                    {devToken && (
                      <div className="space-y-3 rounded-md border border-primary/20 bg-primary/5 p-3 text-sm">
                        <div className="flex items-center gap-2 font-medium text-primary">
                          <KeyRound className="h-4 w-4" />
                          Dev token ready
                        </div>
                        <p className="text-xs text-muted-foreground break-all font-mono">
                          {devToken}
                        </p>
                        <Button
                          type="button"
                          className="w-full gap-2"
                          onClick={() => verifyMut.mutate({ data: { token: devToken } })}
                          disabled={verifyMut.isPending}
                        >
                          {verifyMut.isPending ? "Signing in…" : "Sign in with dev token"}
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </div>
                    )}

                    <Button
                      type="submit"
                      className="w-full"
                      disabled={requestMut.isPending}
                      data-testid="button-send-magic-link"
                    >
                      {requestMut.isPending ? "Sending…" : "Send magic link"}
                    </Button>
                  </form>
                </TabsContent>

                <TabsContent value="request" className="space-y-4 pt-4">
                  {requestState ? (
                    <div className="space-y-4">
                      <div className="flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm">
                        <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-emerald-600" />
                        <div className="space-y-1">
                          <div className="font-medium text-emerald-900">
                            {requestState.status === "approved"
                              ? "You're already approved!"
                              : requestState.alreadyExisted
                                ? "We've already got your request."
                                : "Thanks — we'll email you when you're approved."}
                          </div>
                          <p className="text-emerald-800">
                            {requestState.status === "approved"
                              ? "Head back to the Sign in tab to get your magic link."
                              : `We'll send updates to ${requestState.email}.`}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => {
                          setRequestState(null);
                          requestForm.reset();
                        }}
                      >
                        Submit another request
                      </Button>
                    </div>
                  ) : (
                    <form
                      onSubmit={requestForm.handleSubmit(onRequestAccess, onRequestAccessInvalid)}
                      className="space-y-3"
                    >
                      {/* Honeypot: hidden from humans, irresistible to bots. */}
                      <div
                        aria-hidden="true"
                        style={{
                          position: "absolute",
                          left: "-10000px",
                          width: 1,
                          height: 1,
                          overflow: "hidden",
                        }}
                      >
                        <label htmlFor="req-website">Website</label>
                        <input
                          id="req-website"
                          type="text"
                          tabIndex={-1}
                          autoComplete="off"
                          {...requestForm.register("website")}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="req-email">Email *</Label>
                        <Input
                          id="req-email"
                          type="email"
                          placeholder="you@company.com"
                          autoComplete="email"
                          data-testid="input-request-email"
                          {...requestForm.register("email")}
                        />
                        {requestForm.formState.errors.email && (
                          <p className="text-sm text-destructive">
                            {requestForm.formState.errors.email.message}
                          </p>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label htmlFor="req-name">Your name (optional)</Label>
                          <Input
                            id="req-name"
                            placeholder="Jane Smith"
                            autoComplete="name"
                            data-testid="input-request-name"
                            {...requestForm.register("name")}
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="req-company">Company (optional)</Label>
                          <Input
                            id="req-company"
                            placeholder="Acme Federal"
                            autoComplete="organization"
                            data-testid="input-request-company"
                            {...requestForm.register("company")}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label htmlFor="req-uei">UEI (optional)</Label>
                          <Input
                            id="req-uei"
                            placeholder="Optional"
                            className="font-mono"
                            data-testid="input-request-uei"
                            {...requestForm.register("ueiCode")}
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="req-cage">CAGE (optional)</Label>
                          <Input
                            id="req-cage"
                            placeholder="Optional"
                            className="font-mono"
                            data-testid="input-request-cage"
                            {...requestForm.register("cageCode")}
                          />
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="req-note">Anything we should know? (optional)</Label>
                        <Textarea
                          id="req-note"
                          placeholder="Tell us a bit about your firm or how you heard about SPN (optional)"
                          rows={3}
                          data-testid="input-request-note"
                          {...requestForm.register("note")}
                        />
                      </div>
                      <Button
                        type="submit"
                        className="w-full"
                        disabled={createAccessMut.isPending}
                        data-testid="button-submit-request"
                      >
                        {createAccessMut.isPending ? "Submitting…" : "Request free access"}
                      </Button>
                      <p className="text-center text-xs text-muted-foreground">
                        SPN is free to join. We verify each workspace to keep the network trustworthy;
                        an admin usually reviews requests within a business day, then emails a
                        passwordless sign-in link.
                      </p>
                    </form>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
            <CardFooter />
          </Card>
        </div>
      </div>
    </div>
  );
}
